/**
 * Lead Signal Engine — Rule-based classification + LLM fallback
 *
 * Architecture:
 *   1) Source Quality Scoring (rule-based, 0€)
 *   2) Pass A: Rule-based Signal Extraction (patterns, heuristics)
 *   3) Pass B: LLM fallback for low-confidence signals (cached by textHash)
 *   4) Confidence gates: conf < 0.65 → 'unknown'
 *
 * Output: leadSignals object stored on contact document
 */

import { doc, setDoc, collection, getDocs, query, orderBy, limit as firestoreLimit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebaseConfig';
import {
  VALID_INDUSTRIES,
  VALID_OFFER_TYPES,
  VALID_B2B_B2C,
  VALID_SIZE_PROXY,
  SIGNALS_VERSION as _SIGNALS_VERSION,
} from '../shared/enums';

// Re-export for existing consumers
export { VALID_INDUSTRIES, VALID_OFFER_TYPES, VALID_B2B_B2C, VALID_SIZE_PROXY };
export const SIGNALS_VERSION = _SIGNALS_VERSION;

// ══════════════════════════════════════════════
// ENUMS & CONSTANTS
// ══════════════════════════════════════════════
const CONF_THRESHOLD = 0.65;

// ══════════════════════════════════════════════
// SOURCE QUALITY SCORING (Rule-based, 0€)
// ══════════════════════════════════════════════

const BOILERPLATE_PATTERNS = [
  /all rights reserved/i, /cookie/i, /datenschutz/i, /privacy policy/i,
  /impressum/i, /terms of service/i, /terms and conditions/i,
  /copyright \d{4}/i, /agb/i, /©/,
];

const LOW_VALUE_PATTERNS = [
  /linktree/i, /link in bio/i, /dm for collab/i, /follow.*back/i,
  /^https?:\/\//i, /^\s*@\w+\s*$/,
];

/**
 * Scores the quality of a text source (0-100).
 * Higher = more useful for classification.
 */
export function scoreSourceQuality(text, sourceType = 'unknown') {
  if (!text || typeof text !== 'string') return { score: 0, reasons: ['empty'] };

  const clean = text.replace(/\s+/g, ' ').trim();
  const len = clean.length;
  const reasons = [];
  let score = 50; // baseline

  // Length scoring
  if (len < 30) { score -= 30; reasons.push('very_short'); }
  else if (len < 100) { score -= 15; reasons.push('short'); }
  else if (len < 250) { score -= 5; }
  else if (len >= 1000) { score += 20; reasons.push('very_rich'); }
  else if (len >= 500) { score += 15; reasons.push('rich_text'); }

  // Boilerplate detection
  const boilerplateHits = BOILERPLATE_PATTERNS.filter(p => p.test(clean)).length;
  if (boilerplateHits >= 3) { score -= 20; reasons.push('heavy_boilerplate'); }
  else if (boilerplateHits >= 1) { score -= 5; }

  // Low-value pattern detection
  const lowValueHits = LOW_VALUE_PATTERNS.filter(p => p.test(clean)).length;
  if (lowValueHits >= 2) { score -= 25; reasons.push('low_value_content'); }
  else if (lowValueHits >= 1) { score -= 10; }

  // Hashtag spam (common in Instagram bios)
  const hashtagCount = (clean.match(/#\w+/g) || []).length;
  const wordCount = clean.split(/\s+/).length;
  if (wordCount > 0 && hashtagCount / wordCount > 0.4) {
    score -= 20; reasons.push('hashtag_spam');
  }

  // Emoji density
  const emojiCount = (clean.match(/[\u{1F600}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu) || []).length;
  if (wordCount > 0 && emojiCount / wordCount > 0.3) {
    score -= 10; reasons.push('emoji_heavy');
  }

  // Source type bonus
  const sourceBonus = {
    website_about: 20, website_services: 20, website_home: 10,
    instagram_bio: -5, deep_search: 5, manual: 10, description: 5,
    web_research: 25,
  };
  score += sourceBonus[sourceType] || 0;

  // Keyword richness (business-relevant terms)
  const bizKeywords = /\b(agency|agentur|consulting|software|saas|service|team|clients|kunden|unternehmen|company|business|enterprise|studio|partner|solutions|dienstleistung|beratung|marketing|sales|coaching|training|digital)\b/gi;
  const bizHits = (clean.match(bizKeywords) || []).length;
  if (bizHits >= 3) { score += 10; reasons.push('keyword_rich'); }
  else if (bizHits >= 1) { score += 5; }

  return {
    score: Math.max(0, Math.min(100, Math.round(score))),
    reasons,
    charCount: len,
    wordCount,
  };
}

// ══════════════════════════════════════════════
// TEXT PREPARATION
// ══════════════════════════════════════════════

function prepareText(contact) {
  const parts = [];
  const sourcesUsed = [];

  const isResearchEnriched = !!(contact.spy_last_updated || contact.spy_confidence_score > 0);

  if (contact.lead_description) {
    parts.push(contact.lead_description);
    sourcesUsed.push(isResearchEnriched ? 'web_research' : 'description');
  }
  if (contact.business_name) {
    parts.push(contact.business_name);
    sourcesUsed.push('business_name');
  }
  if (contact.lead_instagram_category) {
    parts.push(contact.lead_instagram_category);
    sourcesUsed.push('instagram_category');
  }
  if (contact.lead_type) {
    parts.push(contact.lead_type);
    sourcesUsed.push('lead_type');
  }
  if (contact.lead_website) {
    parts.push(contact.lead_website);
    sourcesUsed.push('website_url');
  }
  if (contact.name) {
    parts.push(contact.name);
    sourcesUsed.push('name');
  } else if (contact.first_name) {
    parts.push([contact.first_name, contact.last_name].filter(Boolean).join(' '));
    sourcesUsed.push('name');
  }
  if (contact.lead_email) {
    const domain = String(contact.lead_email).split('@')[1] || '';
    if (domain && !['gmail.com','yahoo.com','hotmail.com','outlook.com','icloud.com','gmx.de','web.de','t-online.de','aol.com','mail.com','protonmail.com','live.com'].includes(domain.toLowerCase())) {
      parts.push(domain);
      sourcesUsed.push('email_domain');
    }
  }
  if (contact.lead_address || contact.lead_city || contact.lead_country) {
    const geo = [contact.lead_city, contact.lead_country].filter(Boolean).join(' ');
    if (geo) { parts.push(geo); sourcesUsed.push('geo'); }
  }
  if (contact.lead_socials) {
    parts.push(contact.lead_socials);
    sourcesUsed.push('socials');
  }
  if (contact.lead_title) {
    parts.push(contact.lead_title);
    sourcesUsed.push('job_title');
  }
  if (contact.lead_position) {
    parts.push(contact.lead_position);
    sourcesUsed.push('position');
  }
  if (contact.lead_company) {
    parts.push(contact.lead_company);
    sourcesUsed.push('company');
  }
  if (contact.lead_linkedin_headline) {
    parts.push(contact.lead_linkedin_headline);
    sourcesUsed.push('linkedin_headline');
  }

  // Decision Maker roles (structured signal data)
  if (contact.lead_decision_makers) {
    try {
      const dms = typeof contact.lead_decision_makers === 'string'
        ? JSON.parse(contact.lead_decision_makers)
        : contact.lead_decision_makers;
      if (Array.isArray(dms)) {
        const roles = dms.map(dm => dm.role || dm.position).filter(Boolean);
        if (roles.length > 0) {
          parts.push(roles.join(', '));
          sourcesUsed.push('decision_maker_roles');
        }
      }
    } catch (_) {}
  }

  // Research intelligence (structured data from web research)
  const ri = contact.researchIntelligence;
  if (ri) {
    if (ri.companyInfo) {
      parts.push(ri.companyInfo);
      sourcesUsed.push('research_company_info');
    }
    if (ri.reasoning) {
      parts.push(ri.reasoning);
      sourcesUsed.push('research_reasoning');
    }
    if (ri.budgetIndicators) {
      parts.push(ri.budgetIndicators);
      sourcesUsed.push('research_budget');
    }
  }

  const raw = parts.join(' | ').substring(0, 3000);
  const lower = raw.toLowerCase();

  return { raw, lower, sourcesUsed };
}

// ══════════════════════════════════════════════
// PASS A: RULE-BASED SIGNAL EXTRACTION
// ══════════════════════════════════════════════

// ── Industry Detection ──

// STRICT RULES: Only patterns that UNIQUELY identify an industry.
// Ambiguous terms → "unknown" → LLM decides. Better safe than wrong.
const INDUSTRY_RULES = [
  { industry: 'SaaS', patterns: [/\bsaas\b/i, /\bsoftware as a service\b/i, /\bcloud.?platform\b/i, /\bapp\s*entwicklung\b/i], weight: 0.85 },
  { industry: 'E-Commerce', patterns: [/\be[-\s]?commerce\b/i, /\bonline[-\s]?shop\b/i, /\bshopify\b/i, /\bwoocommerce\b/i, /\bmagento\b/i, /\bonline[-\s]?store\b/i, /\bwebshop\b/i], weight: 0.85 },
  { industry: 'Agency', patterns: [/\bkreativ[-\s]?agentur\b/i, /\bdigital[-\s]?agency\b/i, /\bwerbeagentur\b/i, /\bfull[-\s]?service[-\s]?agentur\b/i, /\bmedia[-\s]?agentur\b/i, /\bpr[-\s]?agentur\b/i], weight: 0.85 },
  { industry: 'Marketing', patterns: [/\bmarketing\s*(agentur|agency|beratung|consulting)\b/i, /\bseo[-\s]?(agentur|agency|beratung|optimierung)\b/i, /\bsocial\s*media\s*marketing\b/i, /\bcontent\s*marketing\b/i, /\bperformance\s*marketing\b/i, /\bgoogle\s*ads?\b/i, /\blead[-\s]?generierung\b/i, /\bonline[-\s]?marketing\b/i], weight: 0.80 },
  { industry: 'Consulting', patterns: [/\bunternehmensberatung\b/i, /\bmanagement[-\s]?consulting\b/i, /\bstrategie[-\s]?beratung\b/i, /\bconsulting\s*(firm|company|gmbh|gruppe)\b/i], weight: 0.85 },
  { industry: 'Real Estate', patterns: [/\bimmobilien/i, /\breal\s*estate\b/i, /\bmakler(?:in)?\b/i, /\bproperty\s*(management|invest)/i, /\brealtors?\b/i, /\bhausverwaltung\b/i], weight: 0.85 },
  { industry: 'Finance', patterns: [/\bfinanz(beratung|dienstleist|verwaltung)/i, /\bfinancial\s*(services|advisory|planning)\b/i, /\bfintech\b/i, /\bvermögens(verwaltung|beratung)/i, /\bsteuerberater(in)?\b/i, /\bsteuerkanzlei\b/i, /\bwirtschaftsprüf/i, /\bbuchhaltung(s[-\s]?(büro|service))?\b/i], weight: 0.85 },
  { industry: 'Healthcare', patterns: [/\bhealthcare\b/i, /\bmedizin(isch|technik)\b/i, /\bmedical\b/i, /\b[äa]rzt(praxis|in)\b/i, /\bklinik\b/i, /\b(arzt|zahn|tier|kinder)praxis\b/i, /\bpharmac/i, /\bdentist/i, /\bzahnarzt/i, /\bkrankenhaus\b/i, /\bheilpraktik/i, /\bpsychotherap/i, /\btherapiepraxis\b/i, /\bhypnotherap/i, /\boptik(er|erin|geschäft)?\b/i, /\bapotheke\b/i, /\bpharmacy\b/i, /\blogopäd/i, /\bergotherap/i, /\bosteopath/i, /\bchiropraktik/i, /\bchiropract/i, /\bpflegedienst\b/i, /\bambulant(er|e)?\s*pflege/i, /\bhebamme\b/i, /\bmidwi(fe|ves)\b/i], weight: 0.85 },
  { industry: 'Education', patterns: [/\beducation\b/i, /\buniversit[äy]/i, /\bakademie\b/i, /\bacademy\b/i, /\be[-\s]?learning\b/i, /\bnachhilfe\b/i, /\bbildungsinstitut/i, /\bsprachschule\b/i, /\bfahrschule\b/i, /\bdriving\s*school\b/i, /\bkita\b/i, /\bkindergarten\b/i, /\bkindertages(stätte|pflege)\b/i, /\bmusikschule\b/i, /\bvolkshochschule\b/i, /\bweiterbildung(s[-\s]?(institut|zentrum|anbieter))?\b/i, /\btraining(s[-\s]?(center|zentrum|institut))?\b/i, /\btutoring\b/i], weight: 0.80 },
  { industry: 'Manufacturing', patterns: [/\bmanufactur/i, /\bproduktion\s*s?[-\s]?(unternehmen|firma|betrieb)\b/i, /\bherstell(er|ung)\b/i, /\bfertigung/i, /\bindustrie(betrieb|unternehmen)/i, /\bfactory\b/i, /\bfabrik\b/i], weight: 0.85 },
  { industry: 'Retail', patterns: [/\bretail(er|ing)\b/i, /\beinzelhandel/i, /\bfachhandel\b/i, /\bboutique\b/i, /\bfachgeschäft\b/i, /\bladen\b.*\b(eröffn|betreib|inhaber)/i, /\bflagship[-\s]?store\b/i, /\bpop[-\s]?up[-\s]?store\b/i, /\bconvenience\s*store\b/i], weight: 0.75 },
  { industry: 'Food & Beverage', patterns: [/\brestaurant\b/i, /\bgastronomie\b/i, /\bcatering\b/i, /\bcaf[eé]\b/i, /\bbäckerei\b/i, /\bbakery\b/i, /\bfood\s*(truck|service|industry)\b/i, /\bbrewer[iy]/i, /\bwinery\b/i, /\bweingut\b/i, /\bmetzgerei\b/i, /\bbutcher\b/i, /\bkonditorei\b/i, /\bimbiss\b/i, /\bbiergarten\b/i, /\bpizzeria\b/i, /\bsushi[-\s]?bar\b/i, /\bbar\b.*\b(cocktail|lounge|club)/i, /\bdistillery\b/i, /\bbrennerei\b/i, /\beisdiele\b/i, /\bice\s*cream\b/i, /\blieferdienst\b/i, /\bfood\s*delivery\b/i], weight: 0.85 },
  { industry: 'Fitness & Wellness', patterns: [
    /\bfitness[-\s]?(studio|center|club|trainer|coach)\b/i,
    /\bgym\b/i,
    /\bpersonal[-\s]?train(er|ing)\b/i,
    /\bpilates[-\s]?(studio|kurs|lehrer)\b/i,
    /\byoga[-\s]?(studio|schule|lehrer|retreat|kurs)\b/i,
    /\bwellness[-\s]?(hotel|resort|center|studio|oase)\b/i,
    /\bphysiotherap(ie|eut|iepraxis)\b/i,
    /\bspa[-\s]?(resort|hotel|bereich)\b/i,
  ], weight: 0.85 },
  { industry: 'Legal', patterns: [/\brechtsanwalt/i, /\blegal\s*(services|tech|beratung)\b/i, /\banwalt/i, /\bkanzlei\b/i, /\blawyer\b/i, /\blaw\s*firm\b/i, /\battorney\b/i, /\bnotar\b/i, /\bjurist/i], weight: 0.85 },
  { industry: 'Media & Entertainment', patterns: [/\bmedia\s*(house|company|produktion|verlag)\b/i, /\bfilmproduktion\b/i, /\bvideo[-\s]?produktion\b/i, /\bvideo\s*production\b/i, /\bpodcast(er|erin|ing)?\b/i, /\bmusik(produktion|label|er|erin)\b/i, /\bverlag\b/i, /\bpublish(er|ing)\b/i, /\bmusician\b/i, /\bsinger\b/i, /\bkomponist/i], weight: 0.80 },
  { industry: 'Construction', patterns: [/\bbau(unternehmen|firma|betrieb)\b/i, /\bconstruction\b/i, /\barchitekt(ur|enbüro)\b/i, /\bhandwerk(er|sbetrieb)\b/i, /\brenovier/i, /\bdachdecker\b/i, /\bsanitär\b/i, /\binnenarchitektur/i, /\binterior[-\s]?design/i, /\bkitchen[-\s]?design/i, /\bküchen[-\s]?design/i, /\btiefbau\b/i, /\bhochbau\b/i, /\btrockenbau\b/i, /\bgerüstbau\b/i, /\bstahlbau\b/i, /\bholzbau\b/i, /\bbetonbau\b/i, /\bimmobilien[-\s]?entwickl/i, /\bproperty\s*develop/i, /\bgeneral\s*contractor\b/i, /\bzimmerei\b/i, /\bstuckateur\b/i], weight: 0.85 },
  { industry: 'Automotive', patterns: [/\bauto(haus|händler|werkstatt)\b/i, /\bautomobil/i, /\bautomotive\b/i, /\bcar\s*dealer/i, /\bkfz[-\s]?(werkstatt|meister|betrieb|gutachter)\b/i, /\breifen(service|händler|handel)\b/i, /\btire\s*(shop|service)\b/i, /\bfahrzeug(aufbereitung|pflege|technik)\b/i, /\bcar\s*(wash|detailing|rental|repair)\b/i, /\bautolackier/i, /\bautovermietung\b/i], weight: 0.85 },
  { industry: 'Travel & Tourism', patterns: [/\breise(büro|veranstalter|agentur)\b/i, /\btravel\s*(agency|company|blog)\b/i, /\btourism/i, /\bhotel(lerie)?\b/i, /\bferienwohnung/i, /\bferienhaus\b/i, /\bairbnb\b/i, /\bferienwohnung(en|svermittlung)?\b/i, /\bpension\b/i, /\bgasthof\b/i, /\bgasthaus\b/i, /\bretreat[-\s]?(center|zentrum)\b/i, /\bcamping(platz)?\b/i, /\bhostel\b/i], weight: 0.85 },
  { industry: 'Logistics', patterns: [/\blogistik(unternehmen)?\b/i, /\blogistics\b/i, /\bspedition/i, /\btransportunternehmen\b/i, /\bfulfillment\b/i, /\bumzug(s[-\s]?(unternehmen|firma|service))?\b/i, /\bmoving\s*(company|service)\b/i, /\bkurier(dienst|service)?\b/i, /\bcourier\b/i, /\bwarehouse\b/i, /\blager(logistik|verwaltung|haus)\b/i, /\bsupply\s*chain\b/i, /\blast[-\s]?mile\b/i], weight: 0.85 },
  { industry: 'Insurance', patterns: [/\bversicherung(smakler|sagentur|sberater|sbüro|sgesellschaft)?\b/i, /\binsurance\s*(broker|agent|company|agency)?\b/i, /\bschadens[-\s]?(regulierung|management)\b/i, /\brisikoberatung\b/i, /\binsurtech\b/i], weight: 0.85 },
  { industry: 'Non-Profit', patterns: [/\bnon[-\s]?profit\b/i, /\bgemeinnützig/i, /\bngo\b/i, /\bstiftung\b/i, /\bcharity\b/i, /\bfoundation\b/i], weight: 0.85 },
  { industry: 'Recruiting', patterns: [/\brecruiting\b/i, /\bpersonalberater/i, /\bheadhunt/i, /\bhr[-\s]?tech\b/i, /\btalent\s*acqui/i, /\bstaffing\b/i, /\bpersonalvermittl/i], weight: 0.85 },
  { industry: 'Beauty & Cosmetics', patterns: [/\bbeauty\s*(salon|studio|lounge)\b/i, /\bkosmetik(studio|institut)\b/i, /\bfriseur(salon)?\b/i, /\bhair\s*(salon|stylist|studio)\b/i, /\bnail\s*(studio|salon|designer)\b/i, /\btattoo[-\s]?(studio|artist|shop)\b/i, /\bpiercing[-\s]?(studio|shop)\b/i, /\bbarbershop\b/i, /\bbarber[-\s]?shop\b/i, /\bpermanent[-\s]?make[-\s]?up\b/i, /\bmicroblading\b/i, /\bwimpern(verlängerung|studio)\b/i, /\blash\s*(extension|studio|artist)\b/i, /\bvisagist/i, /\bmake[-\s]?up[-\s]?artist\b/i], weight: 0.80 },
  { industry: 'Photography', patterns: [/\bfotograf/i, /\bphotograph/i, /\bfoto[-\s]?studio\b/i, /\bphoto[-\s]?studio\b/i, /\bvideograph/i, /\bbildbearbeit/i], weight: 0.85 },
  { industry: 'IT Services', patterns: [/\bit[-\s]?service/i, /\bit[-\s]?dienstleist/i, /\bweb[-\s]?develop/i, /\bwebentwickl/i, /\bdevops\b/i, /\bcyber[-\s]?security\b/i, /\bmanaged\s*service/i], weight: 0.85 },
  { industry: 'Design', patterns: [
    /\bdesign[-\s]?agentur\b/i, /\bdesign[-\s]?agency\b/i,
    /\bui[-/\s]?ux/i, /\bux[-\s]?design/i,
    /\bgraphic[-\s]?design/i, /\bgrafik[-\s]?design/i,
    /\bweb[-\s]?design(er|erin)?\b/i, /\bwebdesign(er|erin)?\b/i,
    /\bbrand[-\s]?design/i, /\bcorporate[-\s]?design/i,
    /\blogo[-\s]?design/i, /\bvisual[-\s]?design/i,
    /\bmotion[-\s]?design/i,
  ], weight: 0.85 },
  { industry: 'Coaching', patterns: [
    /\b(life|business|sales|karriere|executive|team|mental|health|mindset|leadership|führungskräfte|systemisch|agil)[-\s]?coach(ing)?\b/i,
    /\bcoaching[-\s]?(praxis|institut|akademie|angebot|programm)\b/i,
    /\bmentor(ing|in)\b/i,
    /\bpersönlichkeitsentwicklung/i,
    /\bcoach\b/i,
  ], weight: 0.85 },
  { industry: 'Events', patterns: [/\bevent[-\s]?(agentur|management|planung|planer)\b/i, /\beveranstaltung/i, /\bkonferenz\b/i, /\bmesse(bau|stand|service)\b/i, /\bhochzeits[-\s]?planer\b/i, /\bwedding\s*(planner|planning)\b/i], weight: 0.85 },
  { industry: 'Architecture', patterns: [/\barchitekt(ur)?[-\s]?(büro|studio|firma)\b/i, /\barchitect(ure)?[-\s]?(firm|office|studio)\b/i, /\barchitekt(in)?\b/i, /\blandschaftsarchitekt/i, /\bstadtplan(ung|er)\b/i, /\burban\s*(planning|design)\b/i, /\braumplan(ung|er)\b/i], weight: 0.85 },
  { industry: 'Energy', patterns: [
    /\bsolar(technik|anlage|energie|strom|modul|panel|install)\b/i,
    /\bphotovoltaik\b/i, /\bphotovoltaic\b/i, /\bpv[-\s]?anlage/i,
    /\benergie(beratung|berater|versorger|dienstleist|wende|konzept|management)\b/i,
    /\benergy\s*(consulting|provider|service|management|solution)\b/i,
    /\bwind(energie|kraft|park)\b/i, /\bwind\s*(energy|power|farm)\b/i,
    /\bwärmepumpe/i, /\bheat\s*pump/i,
    /\belektro[-\s]?mobilität/i, /\be[-\s]?mobility\b/i,
    /\bladesäule/i, /\bcharging\s*station/i,
    /\bnachhaltige\s*energie/i, /\brenewable\s*energy/i,
    /\bstadtwerk[e]?\b/i, /\butility\b/i,
  ], weight: 0.85 },
  { industry: 'Cleaning & Facility', patterns: [
    /\breinigung(s[-\s]?(firma|service|unternehmen|betrieb))?\b/i,
    /\bgebäudereinigung\b/i, /\bfensterreinigung\b/i, /\bteppichreinigung\b/i,
    /\bcleaning\s*(service|company)\b/i,
    /\bfacility[-\s]?management\b/i, /\bhausmeister(service|dienst)?\b/i,
    /\bgebäude(management|verwaltung|service)\b/i,
    /\bjanitorial\b/i, /\bproperty\s*maintenance\b/i,
    /\bschädlingsbekämpf/i, /\bpest\s*control\b/i,
  ], weight: 0.85 },
  { industry: 'Trades & Crafts', patterns: [
    /\belektrik(er|erbetrieb|erunternehmen)\b/i, /\belektromeister\b/i,
    /\belectrician\b/i, /\belectrical\s*contractor\b/i,
    /\bmaler(betrieb|meister|ei)\b/i, /\bpainter\b/i,
    /\bschreinerei\b/i, /\btischlerei\b/i, /\bcarpenter\b/i, /\bcarpentry\b/i,
    /\binstallateur(betrieb)?\b/i, /\bklempner(ei)?\b/i, /\bplumber\b/i, /\bplumbing\b/i,
    /\bschloss(erei|er)\b/i, /\blocksmith\b/i,
    /\bglasermeister\b/i, /\bglaserei\b/i,
    /\bfliesenleger\b/i, /\bfliesenleg/i,
    /\bmeisterbetrieb\b/i,
    /\bgarten[-\s]?und[-\s]?landschaftsbau\b/i, /\bgalabau\b/i, /\blandscaping\b/i,
    /\bgartengestaltung\b/i, /\bgärtner(ei)?\b/i,
  ], weight: 0.85 },
  { industry: 'Agriculture', patterns: [
    /\blandwirtschaft(sbetrieb|licher)?\b/i, /\bagricultur/i,
    /\bbauernhof\b/i, /\bfarm(ing|er|betrieb)?\b/i,
    /\bviehzucht\b/i, /\bvieh(betrieb|haltung)\b/i,
    /\bweinbau\b/i, /\bwinzer\b/i, /\bvineyard\b/i,
    /\bbio[-\s]?(hof|bauer|landwirtschaft|betrieb)\b/i,
    /\bagrar(betrieb|technik|handel)\b/i,
    /\bforstwirtschaft\b/i, /\bforestry\b/i,
  ], weight: 0.85 },
  { industry: 'Pet Industry', patterns: [
    /\bhundetrainer\b/i, /\bhundeschule\b/i, /\bdog\s*train(er|ing)\b/i,
    /\btierpension\b/i, /\bhundesalon\b/i, /\bhundefriseur\b/i,
    /\btierbedarf\b/i, /\btierfutter\b/i, /\bpet\s*(shop|store|food|supply|care)\b/i,
    /\btierheim\b/i, /\btier(pflege|betreuung|sitter)\b/i,
    /\bpet\s*sit(ter|ting)\b/i, /\bdog\s*walk(er|ing)\b/i,
    /\btierarztpraxis\b/i, /\bveterinary\b/i, /\bveterinär/i,
  ], weight: 0.85 },
];

// Negation detection: checks if a keyword is preceded by a negation phrase
const NEGATION_PATTERNS = [
  /\bnicht?\b/i, /\bkein(e|em|en|er)?\b/i, /\bnot?\b/i,
  /\bno\b/i, /\bwithout\b/i, /\bohne\b/i, /\bnever\b/i,
  /\bdon'?t\b/i, /\bdoesn'?t\b/i, /\bwon'?t\b/i,
];

function isNegated(text, matchIndex) {
  const preceding = text.substring(Math.max(0, matchIndex - 30), matchIndex);
  return NEGATION_PATTERNS.some(p => p.test(preceding));
}

function countNonNegatedHits(lower, patterns) {
  let hits = 0;
  for (const pat of patterns) {
    const match = lower.match(pat);
    if (match && !isNegated(lower, match.index)) hits++;
  }
  return hits;
}

function detectIndustry(lower) {
  const matches = [];

  for (const rule of INDUSTRY_RULES) {
    const hitCount = countNonNegatedHits(lower, rule.patterns);
    if (hitCount > 0) {
      const conf = Math.min(0.95, rule.weight + (hitCount - 1) * 0.05);
      matches.push({ industry: rule.industry, conf, hits: hitCount });
    }
  }

  if (matches.length === 0) return { industry: 'unknown', industryConf: 0 };

  matches.sort((a, b) => b.conf - a.conf || b.hits - a.hits);

  // Ambiguity: 2+ industries within 0.15 → penalize, let LLM decide
  if (matches.length >= 2 && matches[0].conf - matches[1].conf < 0.15) {
    matches[0].conf *= 0.70;
  }

  // Single match with a specific compound pattern → trust it
  return {
    industry: matches[0].conf >= CONF_THRESHOLD ? matches[0].industry : 'unknown',
    industryConf: parseFloat(matches[0].conf.toFixed(2)),
    _allMatches: matches.slice(0, 3),
  };
}

// ── Offer Type Detection ──

// STRICT: Only compound terms that uniquely identify offer type.
const OFFER_TYPE_RULES = [
  { type: 'saas', patterns: [/\bsaas\b/i, /\bsoftware as a service\b/i, /\bcloud[-\s]?(software|platform|tool)\b/i], weight: 0.85 },
  { type: 'agency_service', patterns: [/\b\w+agentur\b/i, /\b\w+[-\s]agency\b/i, /\bfull[-\s]?service\b/i], weight: 0.80 },
  { type: 'service', patterns: [/\bdienstleistung/i, /\bconsulting\b/i, /\bfreelance/i, /\bberater(?:in)?\b/i], weight: 0.70 },
  { type: 'coaching', patterns: [/\b\w+[-\s]?coaching\b/i, /\b\w+[-\s]?coach\b/i, /\bmentoring[-\s]?(programm|angebot)\b/i], weight: 0.80 },
  { type: 'education', patterns: [/\bonline[-\s]?kurs/i, /\be[-\s]?learning\b/i, /\bausbildung\b/i, /\bnachhilfe\b/i, /\bworkshop(s)?\b/i, /\bseminar(e)?\b/i, /\bwebinar(e)?\b/i, /\bmastermind\b/i, /\bkurs(e|angebot)?\b/i, /\bgruppenprogramm\b/i, /\bcohort\b/i, /\bbootcamp\b/i], weight: 0.80 },
  { type: 'physical_product', patterns: [/\bherstell(er|ung)\b/i, /\bmanufact/i, /\bhandmade\b/i, /\bonline[-\s]?shop\b/i, /\bversand\b/i, /\bshipping\b/i, /\bmade\s*in\b/i, /\bprodukt(e|ion|linie)?\b/i, /\bd2c\b/i, /\bdirect[-\s]?to[-\s]?consumer\b/i], weight: 0.75 },
  { type: 'marketplace', patterns: [/\bmarketplace\b/i, /\bmarktplatz\b/i, /\bplattform\b/i, /\btwo[-\s]?sided\b/i], weight: 0.80 },
  { type: 'media_content', patterns: [/\bverlag\b/i, /\bpublish(er|ing)\b/i, /\bpodcast\b/i, /\bvideo[-\s]?produktion\b/i, /\bcontent[-\s]?agentur\b/i, /\bredaktion\b/i, /\beditorial\b/i], weight: 0.75 },
  { type: 'subscription', patterns: [/\bmitgliedschaft(en)?\b/i, /\bmembership\b/i, /\babonnement\b/i, /\bsubscription\b/i, /\bmonatlich(e|er|es)?\s*(beitrag|gebühr|zugang)\b/i, /\bmonthly\s*(fee|plan|access)\b/i, /\babo[-\s]?(box|modell)\b/i, /\brecurring\b/i], weight: 0.80 },
  { type: 'recruiting', patterns: [/\brecruiting\b/i, /\bpersonalvermittl/i, /\bstaffing\b/i], weight: 0.85 },
  { type: 'financial_service', patterns: [/\bfinanz(beratung|dienstleist)/i, /\bsteuerberater/i, /\bbuchhaltung\b/i], weight: 0.80 },
  { type: 'healthcare_service', patterns: [/\barztpraxis\b/i, /\bklinik\b/i, /\bzahnarzt/i, /\btherapiepraxis\b/i], weight: 0.85 },
  { type: 'legal_service', patterns: [/\bkanzlei\b/i, /\blaw\s*firm\b/i, /\banwalt/i, /\bnotar\b/i], weight: 0.85 },
];

function detectOfferType(lower) {
  const matches = [];

  for (const rule of OFFER_TYPE_RULES) {
    const hitCount = countNonNegatedHits(lower, rule.patterns);
    if (hitCount > 0) {
      const conf = Math.min(0.95, rule.weight + (hitCount - 1) * 0.05);
      matches.push({ type: rule.type, conf, hits: hitCount });
    }
  }

  if (matches.length === 0) return { offerType: 'unknown', offerTypeConf: 0 };
  matches.sort((a, b) => b.conf - a.conf || b.hits - a.hits);

  if (matches.length >= 2 && matches[0].conf - matches[1].conf < 0.15) {
    matches[0].conf *= 0.70;
  }

  return {
    offerType: matches[0].conf >= CONF_THRESHOLD ? matches[0].type : 'unknown',
    offerTypeConf: parseFloat(matches[0].conf.toFixed(2)),
  };
}

// ── B2B / B2C Detection ──

const B2B_SIGNALS = [
  /\bb2b\b/i, /\bfür\s*unternehmen\b/i, /\bfor\s*(companies|businesses|teams|enterprises|agencies|startups)\b/i,
  /\bunternehmens/i, /\bcorporate\b/i, /\benterprise\b/i, /\bbusiness\s*solution/i,
  /\bfirmen/i, /\bgeschäftskunden\b/i, /\bwholesale\b/i,
];

const B2C_SIGNALS = [
  /\bb2c\b/i, /\bfür\s*(frauen|männer|mütter|familien|eltern|kinder|verbraucher|privatperson)\b/i,
  /\bfor\s*(women|men|individuals|consumers|families|people|everyone)\b/i,
  /\bprivatkunden\b/i, /\bendverbraucher\b/i,
];

// Industries that are inherently B2B (used as fallback when explicit signals are absent)
const IMPLICIT_B2B_INDUSTRIES = new Set([
  'Agency', 'Consulting', 'IT Services', 'Recruiting', 'Logistics',
  'Manufacturing', 'Insurance', 'Marketing', 'SaaS', 'Cleaning & Facility',
]);
const IMPLICIT_B2C_INDUSTRIES = new Set([
  'Beauty & Cosmetics', 'Fitness & Wellness', 'Photography', 'Pet Industry',
]);
const IMPLICIT_BOTH_INDUSTRIES = new Set([
  'Finance', 'Legal', 'Healthcare', 'Real Estate', 'Coaching', 'Education',
  'Energy', 'Trades & Crafts', 'Architecture', 'Construction',
  'Food & Beverage', 'Agriculture',
]);

function detectB2bB2c(lower, detectedIndustry) {
  const b2bHits = B2B_SIGNALS.filter(p => p.test(lower)).length;
  const b2cHits = B2C_SIGNALS.filter(p => p.test(lower)).length;

  // Explicit signals take priority
  if (b2bHits > 0 || b2cHits > 0) {
    if (b2bHits > 0 && b2cHits > 0) {
      return { b2b_b2c: 'both', b2bB2cConf: 0.65 };
    }
    if (b2bHits > 0) {
      const conf = Math.min(0.95, 0.70 + (b2bHits - 1) * 0.08);
      return { b2b_b2c: 'b2b', b2bB2cConf: parseFloat(conf.toFixed(2)) };
    }
    const conf = Math.min(0.95, 0.70 + (b2cHits - 1) * 0.08);
    return { b2b_b2c: 'b2c', b2bB2cConf: parseFloat(conf.toFixed(2)) };
  }

  // Fallback: implicit B2B/B2C from detected industry (lower confidence)
  if (detectedIndustry && detectedIndustry !== 'unknown') {
    if (IMPLICIT_B2B_INDUSTRIES.has(detectedIndustry)) {
      return { b2b_b2c: 'b2b', b2bB2cConf: 0.60 };
    }
    if (IMPLICIT_B2C_INDUSTRIES.has(detectedIndustry)) {
      return { b2b_b2c: 'b2c', b2bB2cConf: 0.55 };
    }
    if (IMPLICIT_BOTH_INDUSTRIES.has(detectedIndustry)) {
      return { b2b_b2c: 'both', b2bB2cConf: 0.50 };
    }
  }

  return { b2b_b2c: 'unknown', b2bB2cConf: 0 };
}

// ── Size Proxy Detection ──

const SIZE_RULES = {
  SOLO: { patterns: [
    /\bfreelance(r|rin)?\b/i, /\bfreiberufler(in)?\b/i, /\bsolopreneur(in)?\b/i,
    /\bselbstständig/i, /\bselbständig/i, /\bone[-\s]?(man|woman|person)\b/i,
    /\beinzelunternehm/i, /\bindependent\s*(professional|consultant|contractor)\b/i,
    /\bsolo[-\s]?(selbstständig|founder|gründer)\b/i, /\beinzelperson\b/i,
    /\b1[-\s]?(man|frau|person)[-\s]?(betrieb|unternehmen|firma|show)\b/i,
  ], weight: 0.75 },
  SMALL: { patterns: [
    /\bkleines?\s*team\b/i, /\bsmall\s*team\b/i, /\bkleinunternehm/i,
    /\bfamily\s*business/i, /\bfamilienunternehm/i, /\bfamiliengeführt/i,
    /\binhabergeführt/i, /\bowner[-\s]?operat/i,
    /\b[2-9]\s*mitarbeiter/i, /\b[2-9]\s*employees/i,
    /\bgründer[-\s]?team\b/i, /\bkleinstunternehm/i,
    /\bboutique[-\s]?(agentur|agency|studio|firma)\b/i,
  ], weight: 0.70 },
  MID: { patterns: [
    /\bteam\s*(of|von)\s*\d{2}/i, /\b\d{2,}\s*mitarbeiter/i, /\b\d{2,}\s*employees/i,
    /\bmittelständ/i, /\bmittelstand\b/i,
    /\b(über|over|more\s*than)\s*\d{2}\s*(mitarbeiter|employees|angestellte|kollegen)\b/i,
    /\b\d{2,}\+?\s*köpfig/i, /\bwachsendes?\s*team\b/i, /\bgrowing\s*team\b/i,
    /\bmehrere\s*standorte\b/i, /\bmultiple\s*locations\b/i,
  ], weight: 0.70 },
  ENT: { patterns: [
    /\benterprise\b/i, /\bcorporation\b/i, /\bkonzern\b/i,
    /\b\d{3,}\s*(mitarbeiter|employees|angestellte)/i, /\bfortune\b/i,
    /\bgroup\b.*\b(gmbh|ag|inc|ltd)\b/i,
    /\bmultinational/i, /\bglobal(es|er|e)?\s*(unternehmen|company|player|leader)\b/i,
    /\bweltweit(er|e|es)?\s*(tätig|präsent|aktiv|führend)\b/i,
    /\bbörsennotiert/i, /\bpublicly\s*traded\b/i,
    /\b(dax|mdax|sdax|nasdaq|nyse)\b/i,
    /\b\d{4,}\s*(mitarbeiter|employees)/i,
    /\bmarktführer\b/i, /\bmarket\s*leader\b/i,
  ], weight: 0.80 },
};

function detectSizeProxy(lower, contact, detectedIndustry) {
  const matches = [];
  for (const [size, rule] of Object.entries(SIZE_RULES)) {
    const hitCount = countNonNegatedHits(lower, rule.patterns);
    if (hitCount > 0) {
      const conf = Math.min(0.95, rule.weight + (hitCount - 1) * 0.05);
      matches.push({ size, conf, hits: hitCount });
    }
  }

  // Instagram followers: only use as WEAK tiebreaker, never primary signal
  // Influencers with 100k followers are still solos — followers != employees
  const followers = parseInt(contact.lead_instagram_followers, 10);
  let igHint = null;
  if (followers > 0) {
    // Very conservative: only use IG as confirmation, not source of truth
    const isLikelyCreator = /\b(influencer|creator|blogger|content\s*creator|coach)\b/i.test(lower);
    if (!isLikelyCreator) {
      if (followers < 300) igHint = 'SOLO';
      else if (followers < 2000) igHint = 'SMALL';
      // Don't infer MID/ENT from followers — too unreliable
    }
  }

  // Industry-based size heuristics (weak fallback)
  const soloLikelyIndustries = new Set(['Coaching', 'Photography', 'Beauty & Cosmetics', 'Trades & Crafts', 'Pet Industry']);

  if (matches.length === 0) {
    // No text-based hits — try fallbacks
    if (igHint) return { sizeProxy: igHint, sizeProxyConf: 0.40 };

    // Industry-based heuristic (very low confidence)
    if (detectedIndustry && soloLikelyIndustries.has(detectedIndustry)) {
      return { sizeProxy: 'SOLO', sizeProxyConf: 0.35 };
    }
    return { sizeProxy: 'unknown', sizeProxyConf: 0 };
  }

  matches.sort((a, b) => b.conf - a.conf || b.hits - a.hits);
  let best = matches[0];

  // IG confirms text-based detection → small boost
  if (igHint && igHint === best.size) {
    best.conf = Math.min(0.95, best.conf + 0.05);
  }

  return {
    sizeProxy: best.conf >= CONF_THRESHOLD ? best.size : 'unknown',
    sizeProxyConf: parseFloat(best.conf.toFixed(2)),
  };
}

// ── Language Detection (lightweight) ──

const LANG_SIGNALS = {
  de: [/\bund\b/, /\bdie\b/, /\bder\b/, /\bdas\b/, /\bist\b/, /\bfür\b/, /\bmit\b/, /\bwir\b/, /\bihr\b/, /\bsind\b/, /\bnicht\b/, /\bauch\b/],
  en: [/\bthe\b/, /\band\b/, /\bfor\b/, /\bwith\b/, /\byou\b/, /\bthat\b/, /\bhave\b/, /\bfrom\b/, /\bthis\b/, /\bbeen\b/, /\bwill\b/],
  fr: [/\bles\b/, /\bdes\b/, /\bpour\b/, /\bune?\b/, /\bavec\b/, /\bnous\b/, /\bvous\b/, /\bsont\b/, /\bdans\b/],
  es: [/\blos\b/, /\bpara\b/, /\bcon\b/, /\buna?\b/, /\bdel\b/, /\bque\b/, /\besta\b/, /\bmás\b/],
  it: [/\bdella\b/, /\bche\b/, /\bnon\b/, /\bcon\b/, /\bper\b/, /\buna\b/, /\bsono\b/, /\bdal\b/],
};

function detectLanguage(lower) {
  const scores = {};
  for (const [lang, patterns] of Object.entries(LANG_SIGNALS)) {
    scores[lang] = patterns.filter(p => p.test(lower)).length;
  }
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  if (sorted[0][1] === 0) return { language: 'unknown', languageConf: 0 };
  const conf = Math.min(0.95, 0.50 + sorted[0][1] * 0.05);
  return { language: sorted[0][0], languageConf: parseFloat(conf.toFixed(2)) };
}

// ── Geo Hint (from existing fields) ──

function detectGeoHint(contact) {
  const city = contact.lead_city || '';
  const country = contact.lead_country || '';
  const postcode = contact.lead_postcode || '';

  if (!city && !country && !postcode) return { geoHint: null, geoHintConf: 0 };

  // Country detection from domain
  let countryHint = country;
  if (!countryHint && contact.lead_website) {
    const tld = contact.lead_website.match(/\.([a-z]{2})(?:\/|$)/i);
    if (tld) {
      const tldMap = { de: 'DE', at: 'AT', ch: 'CH', uk: 'GB', fr: 'FR', es: 'ES', it: 'IT', nl: 'NL', be: 'BE', pl: 'PL', cz: 'CZ', se: 'SE', dk: 'DK', no: 'NO', fi: 'FI', pt: 'PT' };
      countryHint = tldMap[tld[1].toLowerCase()] || '';
    }
  }

  // Region hint from postcode (DACH focus)
  let region = '';
  if (postcode && countryHint === 'DE') {
    const plz = parseInt(postcode, 10);
    if (plz >= 10000 && plz < 15000) region = 'Berlin';
    else if (plz >= 20000 && plz < 22000) region = 'Hamburg';
    else if (plz >= 80000 && plz < 82000) region = 'München';
    else if (plz >= 50000 && plz < 52000) region = 'Köln';
    else if (plz >= 60000 && plz < 64000) region = 'Frankfurt';
    else if (plz >= 70000 && plz < 73000) region = 'Stuttgart';
    else if (plz >= 40000 && plz < 42000) region = 'Düsseldorf';
  }

  const geo = {};
  if (countryHint) geo.country = countryHint;
  if (city) geo.city = city;
  if (region) geo.region = region;

  const conf = (countryHint ? 0.4 : 0) + (city ? 0.4 : 0) + (region ? 0.2 : 0);

  return { geoHint: Object.keys(geo).length > 0 ? geo : null, geoHintConf: parseFloat(conf.toFixed(2)) };
}

// ── Tag Extraction (normalized, max 10) ──

const TAG_PATTERNS = [
  { tag: 'shopify', patterns: [/\bshopify\b/i] },
  { tag: 'wordpress', patterns: [/\bwordpress\b/i, /\bwoocommerce\b/i] },
  { tag: 'meta_ads', patterns: [/\bmeta\s*ads?\b/i, /\bfacebook\s*ads?\b/i, /\binstagram\s*ads?\b/i] },
  { tag: 'google_ads', patterns: [/\bgoogle\s*ads?\b/i, /\bsea[-\s]?(marketing|agentur|agency|kampagne|strateg)\b/i, /\bppc\s*(marketing|campaign|agentur|agency|management)\b/i, /\bsearch\s*ads?\b/i] },
  { tag: 'seo', patterns: [/\bseo\b/i, /\bsearch\s*engine\s*optim/i, /\bsuchmaschinenoptim/i] },
  { tag: 'web_design', patterns: [/\bweb\s*design\b/i, /\bwebdesign\b/i] },
  { tag: 'social_media', patterns: [/\bsocial\s*media\b/i, /\bsocial\s*media\s*management\b/i] },
  { tag: 'content_creation', patterns: [/\bcontent\s*creation\b/i, /\bcontent\s*creat(or|ing|ion)\b/i, /\bcopywriting\b/i, /\bcopywriter\b/i, /\btexter(in)?\b/i, /\bredakteur/i, /\bghost[-\s]?writ/i] },
  { tag: 'lead_generation', patterns: [/\blead\s*gen/i, /\bleadgenerierung\b/i, /\blead\s*acquisition/i] },
  { tag: 'automation', patterns: [/\bautomation\b/i, /\bautomatisierung\b/i, /\bautomate\b/i] },
  { tag: 'crm', patterns: [/\bcrm\b/i, /\bhubspot\b/i, /\bsalesforce\b/i, /\bpipedrive\b/i] },
  { tag: 'ai', patterns: [/\bai[-\s]?(tool|solution|software|agentur|agency|powered|gestützt|basiert|driven)\b/i, /\bartificial\s*intellig/i, /\bkünstliche\s*intellig/i, /\bmachine\s*learn/i, /\bchatbot\b/i, /\bgenerative\s*ai\b/i, /\bki[-\s]?(tool|lösung|beratung|software|gestützt|basiert)\b/i, /\bdeep\s*learn/i] },
  { tag: 'ecommerce', patterns: [/\be.?commerce\b/i, /\bonline.?shop\b/i] },
  { tag: 'b2b_sales', patterns: [/\bb2b\s*sales\b/i, /\boutbound\b/i, /\bcold\s*email/i, /\bcold\s*outreach/i] },
  { tag: 'branding', patterns: [/\bbranding\b/i, /\bbrand\s*strateg/i, /\bbrand\s*identity\b/i] },
  { tag: 'local_business', patterns: [/\blokal(es|er|e)?\s*(unternehmen|geschäft|anbieter|service|dienstleist)\b/i, /\blocal\s*(business|service|shop|provider)\b/i, /\bregional(es|er|e)?\s*(unternehmen|anbieter|dienstleist|betrieb)\b/i, /\bvor\s*ort\b/i, /\bin\s*der\s*(region|nähe|umgebung)\b/i] },
  { tag: 'premium', patterns: [/\bpremium\b/i, /\bluxury\b/i, /\bluxus\b/i, /\bhigh.?end\b/i] },
  { tag: 'startup', patterns: [/\bstartup\b/i, /\bstart.?up\b/i, /\bgründer\b/i, /\bfounder\b/i] },
  { tag: 'sustainability', patterns: [/\bnachhaltig(keit|e|es|er)?\b/i, /\bsustainab(le|ility)\b/i, /\beco[-\s]?friendly\b/i, /\bumwelt(freundlich|bewusst|schutz|management)\b/i, /\bgreen[-\s]?(energy|business|tech|company)\b/i, /\bklima(schutz|neutral|freundlich|wandel)\b/i, /\bfair[-\s]?trade\b/i, /\bzero[-\s]?waste\b/i, /\bcircular\s*economy\b/i] },
  { tag: 'data_analytics', patterns: [/\bdata\s*analy(tics|sis|st|se)\b/i, /\bdatenanalyse\b/i, /\bbusiness\s*intelligence\b/i, /\bbi[-\s]?(tool|dashboard|lösung|software|reporting)\b/i, /\bdata[-\s]?(science|engineering|warehouse|driven)\b/i, /\bpower\s*bi\b/i, /\btableau\b/i, /\blooker\b/i] },
  { tag: 'healing', patterns: [/\bheilung/i, /\bhealing\b/i, /\bheilkraft/i, /\bheilungsweg/i, /\bnaturheil/i, /\bheilpraktik/i, /\benergiearbeit/i, /\benergy\s*work/i, /\blichtarbeit/i] },
  { tag: 'spirituality', patterns: [/\bspirituel/i, /\bspiritual/i, /\besoterik/i, /\besoteric/i, /\bschamani/i, /\bshamani/i, /\bastrolog/i, /\btarot\b/i, /\bkartenlegen/i, /\boracle\b/i, /\borakel\b/i, /\bmedial\b/i, /\bmedium\b/i, /\bchanneling/i] },
  { tag: 'meditation', patterns: [/\bmeditation/i, /\bachtsam/i, /\bmindful/i, /\batemarbeit/i, /\batemp/i, /\bbreathing/i, /\bbreath\s*work/i] },
  { tag: 'yoga', patterns: [/\byoga\b/i, /\byogalehrer/i, /\bpilates\b/i, /\bqi\s*gong\b/i, /\btai\s*chi\b/i] },
  { tag: 'ayurveda', patterns: [/\bayurveda/i, /\bayurvedisch/i, /\btcm\b/i] },
  { tag: 'coaching', patterns: [/\bcoaching[-\s]?(programm|angebot|session|praxis|business|online)\b/i, /\b(life|business|sales|karriere|executive|mental|health|mindset|leadership)[-\s]?coach(ing)?\b/i, /\bmentor(ing|in)?\b/i, /\btransformations[-\s]?(coaching|programm|begleitung)\b/i, /\bempowerment[-\s]?(coaching|programm)\b/i, /\bmindset[-\s]?(coaching|shift|training|arbeit)\b/i, /\bpersönlichkeitsentwicklung/i] },
  { tag: 'personal_branding', patterns: [/\bpersonal[-\s]?branding/i, /\bpersonal\s*brand\b/i, /\bselbstdarstellung/i, /\bsichtbarkeit/i, /\bpersonenmarke/i] },
  { tag: 'photography', patterns: [/\bfotograf/i, /\bphotograph/i, /\bfoto[-\s]?studio/i, /\bvideograph/i, /\bbildbearbeit/i] },
  { tag: 'therapy', patterns: [/\btherapie\b/i, /\btherapy\b/i, /\bpsychotherap/i, /\bkörpertherapie/i, /\bheiltherapie/i, /\bhypnos/i, /\bhypnother/i] },
  { tag: 'music', patterns: [/\bmusik(er|erin|produktion|label|schule|unterricht|lehrer)\b/i, /\bmusic\s*(produc|label|school|teach|lesson|studio)\b/i, /\bmusician\b/i, /\bgesang(s[-\s]?(unterricht|lehrer|studio))?\b/i, /\bsinger[-\s]?songwriter\b/i, /\bkomponist/i, /\bsongwriter\b/i, /\bdj\b/i] },
  { tag: 'family', patterns: [/\bfamilie/i, /\bfamily\b/i, /\beltern/i, /\bparent/i, /\bkinder/i, /\bchildren/i, /\bmutter/i, /\bmütter/i, /\bbaby\b/i, /\bschwanger/i, /\bschlafberatung/i] },
  { tag: 'email_marketing', patterns: [/\bemail[-\s]?marketing\b/i, /\bnewsletter\b/i, /\bmailchimp\b/i, /\bklaviyo\b/i, /\bactivecampaign\b/i, /\bemail[-\s]?campaign/i, /\bemail[-\s]?automat/i, /\bdrip[-\s]?campaign/i] },
  { tag: 'influencer', patterns: [/\binfluencer\b/i, /\bcreator[-\s]?economy\b/i, /\bugc[-\s]?creator\b/i, /\bcontent[-\s]?creator\b/i, /\bblogger(in)?\b/i, /\byoutuber\b/i, /\btiktoker\b/i, /\binstagrammer\b/i] },
  { tag: 'video', patterns: [/\bvideo[-\s]?(marketing|produktion|production|editing|content|ads?)\b/i, /\byoutube[-\s]?(kanal|channel|marketing)\b/i, /\btiktok[-\s]?(marketing|strategy|content)\b/i, /\breels?\b/i, /\bshort[-\s]?form[-\s]?video/i] },
  { tag: 'amazon', patterns: [/\bamazon\s*(fba|seller|vendor|shop|store|marketplace|agentur|agency)\b/i, /\bfba\b/i, /\bamazon[-\s]?ppc\b/i] },
  { tag: 'mobile_app', patterns: [/\bapp[-\s]?(entwicklung|development|developer|agentur|agency)\b/i, /\bmobile[-\s]?app\b/i, /\bios[-\s]?app\b/i, /\bandroid[-\s]?app\b/i, /\bflutter\b/i, /\breact[-\s]?native\b/i] },
  { tag: 'real_estate', patterns: [/\bimmobilien/i, /\breal[-\s]?estate\b/i, /\bmakler/i, /\bproperty\b/i, /\bhausverwaltung\b/i, /\bferienwohnung/i] },
  { tag: 'hr_recruiting', patterns: [/\bhr[-\s]?(tech|software|beratung|consulting)\b/i, /\brecruiting\b/i, /\bpersonalberater/i, /\btalent[-\s]?(acquisition|management)\b/i, /\bheadhunt/i, /\bemployer[-\s]?branding\b/i] },
];

function extractTags(lower) {
  const tags = [];
  for (const rule of TAG_PATTERNS) {
    if (countNonNegatedHits(lower, rule.patterns) > 0) {
      tags.push(rule.tag);
    }
  }
  return [...new Set(tags)].slice(0, 10);
}

// ══════════════════════════════════════════════
// PASS R: RESEARCH-BASED SIGNAL EXTRACTION
// Reads structured researchIntelligence data (from web research)
// and produces high-confidence signals without regex or LLM.
// ══════════════════════════════════════════════

/**
 * Parses an AI-generated companySizeEstimate string into VALID_SIZE_PROXY.
 * Handles: ranges ("10-50"), plus ("200+"), bare numbers ("15"),
 * German ("ca. 200 Mitarbeiter", "über 50"), keywords ("Freelancer").
 */
function parseSizeFromEstimate(estimate) {
  if (!estimate || typeof estimate !== 'string') return null;
  const est = estimate.toLowerCase().trim();

  if (/\b(solo|freelanc|selbst|solopren|einzelunternehm|one[- ]?(man|woman|person))\b/.test(est)) return { size: 'SOLO', conf: 0.90 };
  if (/\b(enterprise|konzern|corporation|großunternehm)\b/.test(est)) return { size: 'ENT', conf: 0.88 };
  if (/\b(small\s*team|kleines?\s*team)\b/.test(est)) return { size: 'SMALL', conf: 0.85 };
  if (/\b(medium|mittelständ)\b/.test(est)) return { size: 'MID', conf: 0.82 };

  const numbers = est.match(/\d+/g);
  if (!numbers || numbers.length === 0) return null;

  const hasPlus = /[+]|über|more\s*than|greater|mindestens|at\s*least/.test(est);
  let ref;
  if (numbers.length >= 2) {
    ref = parseInt(numbers[numbers.length - 1], 10);
  } else {
    ref = parseInt(numbers[0], 10);
    if (hasPlus) ref = Math.round(ref * 1.5);
  }
  if (isNaN(ref) || ref <= 0) return null;

  if (ref <= 1) return { size: 'SOLO', conf: 0.88 };
  if (ref <= 10) return { size: 'SMALL', conf: 0.88 };
  if (ref <= 200) return { size: 'MID', conf: 0.88 };
  return { size: 'ENT', conf: 0.88 };
}

/**
 * Extracts signals from structured research intelligence data.
 * Returns only fields where research provides actionable data.
 * Confidence: 0.85-0.90 (higher than rules, lower than human).
 */
function extractSignalsFromResearch(contactData) {
  const ri = contactData.researchIntelligence;
  if (!ri || ri.version == null) return null;

  const signals = {};
  const provenance = {};

  // ── Size from companySizeEstimate (most reliable structured signal) ──
  const sizeResult = parseSizeFromEstimate(ri.companySizeEstimate);
  if (sizeResult) {
    signals.sizeProxy = sizeResult.size;
    signals.sizeProxyConf = sizeResult.conf;
    provenance.sizeProxy = 'research';
  }

  // ── Size heuristic from decision maker count (backup) ──
  if (!signals.sizeProxy && ri.decisionMakerCount > 0) {
    if (ri.decisionMakerCount >= 7) {
      signals.sizeProxy = 'ENT';
      signals.sizeProxyConf = 0.80;
      provenance.sizeProxy = 'research_dm';
    } else if (ri.decisionMakerCount >= 4) {
      signals.sizeProxy = 'MID';
      signals.sizeProxyConf = 0.78;
      provenance.sizeProxy = 'research_dm';
    } else if (ri.decisionMakerCount >= 2) {
      signals.sizeProxy = 'SMALL';
      signals.sizeProxyConf = 0.70;
      provenance.sizeProxy = 'research_dm';
    }
  }

  // ── C-suite roles → likely MID+ (boost existing size if SOLO/SMALL) ──
  if (Array.isArray(ri.decisionMakerRoles) && ri.decisionMakerRoles.length > 0) {
    const rolesLower = ri.decisionMakerRoles.map(r => r.toLowerCase());
    const hasCLevel = rolesLower.some(r =>
      /\b(cto|cfo|coo|cmo|cpo|vp|vice\s*president|director|head\s*of|geschäftsführ|vorstand)\b/i.test(r)
    );
    if (hasCLevel && signals.sizeProxy === 'SOLO') {
      signals.sizeProxy = 'SMALL';
      signals.sizeProxyConf = 0.75;
    }
    if (hasCLevel && !signals.sizeProxy) {
      signals.sizeProxy = 'MID';
      signals.sizeProxyConf = 0.72;
      provenance.sizeProxy = 'research_dm';
    }

    // DM role → B2B heuristic (Sales, Account, BD roles)
    const hasB2bRoles = rolesLower.some(r =>
      /\b(sales|account|business\s*develop|vertrieb|b2b|partner|key\s*account|enterprise)\b/i.test(r)
    );
    if (hasB2bRoles && !signals.b2b_b2c) {
      signals.b2b_b2c = 'b2b';
      signals.b2bB2cConf = 0.72;
      provenance.b2b_b2c = 'research_dm';
    }
  }

  return Object.keys(signals).length > 0 ? { signals, provenance } : null;
}

// ══════════════════════════════════════════════
// NANO LLM FALLBACK — Backend Cloud Function (SalesGod API key)
// Global learned patterns, per-user monthly budget tracking.
// ══════════════════════════════════════════════

const _classifyNano = httpsCallable(functions, 'classifyContactNano');

let _budgetExceeded = false;
let _budgetExceededLoggedAt = 0;

export function resetBudgetExceededFlag() {
  _budgetExceeded = false;
  _budgetExceededLoggedAt = 0;
}

export function resetLazyProcessed() {
  _lazyProcessed.clear();
}

async function classifyViaNanoBackend(text, contactId) {
  if (_budgetExceeded) return null;

  try {
    const result = await _classifyNano({ text, contactId });
    const { signals, source, usage } = result.data || {};
    if (source === 'budget_exceeded') {
      _budgetExceeded = true;
      if (Date.now() - _budgetExceededLoggedAt > 60000) {
        console.warn('[Intel] Monthly LLM budget exceeded:', usage);
        _budgetExceededLoggedAt = Date.now();
      }
      return null;
    }
    return signals || null;
  } catch (e) {
    console.warn('[Intel] Nano LLM backend call failed:', e.message);
    return null;
  }
}

// ══════════════════════════════════════════════
// PASS A: FULL RULE-BASED EXTRACTION
// ══════════════════════════════════════════════

/**
 * Runs all rule-based signal extractors on a contact.
 * Returns a leadSignals object with confidence scores.
 */
export function extractSignalsRuleBased(contact) {
  const { raw, lower, sourcesUsed } = prepareText(contact);

  if (!raw || raw.length < 10) {
    return {
      industry: 'unknown', industryConf: 0,
      offerType: 'unknown', offerTypeConf: 0,
      b2b_b2c: 'unknown', b2bB2cConf: 0,
      sizeProxy: 'unknown', sizeProxyConf: 0,
      geoHint: null, geoHintConf: 0,
      language: 'unknown', languageConf: 0,
      tags: [],
      signalsVersion: SIGNALS_VERSION,
      computedAt: Date.now(),
      sourcesUsed,
      extractionMethod: 'rules',
      _sourceQuality: scoreSourceQuality(raw, sourcesUsed[0]),
      _needsLlm: true,
    };
  }

  const sourceQuality = scoreSourceQuality(raw, sourcesUsed[0]);
  const industryResult = detectIndustry(lower);
  const offerTypeResult = detectOfferType(lower);
  const detectedInd = industryResult.industryConf >= CONF_THRESHOLD ? industryResult.industry : null;
  const b2bResult = detectB2bB2c(lower, detectedInd);
  const sizeResult = detectSizeProxy(lower, contact, detectedInd);
  const langResult = detectLanguage(lower);
  const geoResult = detectGeoHint(contact);
  const tags = extractTags(lower);

  // Determine if LLM fallback is needed
  const lowConfFields = [];
  if (industryResult.industryConf < CONF_THRESHOLD) lowConfFields.push('industry');
  if (offerTypeResult.offerTypeConf < CONF_THRESHOLD) lowConfFields.push('offerType');
  if (b2bResult.b2bB2cConf < CONF_THRESHOLD) lowConfFields.push('b2b_b2c');
  if (sizeResult.sizeProxyConf < CONF_THRESHOLD) lowConfFields.push('sizeProxy');

  const needsLlm = lowConfFields.length >= 2 && sourceQuality.score >= 30;

  return {
    ...industryResult,
    ...offerTypeResult,
    ...b2bResult,
    ...sizeResult,
    ...geoResult,
    ...langResult,
    tags,
    signalsVersion: SIGNALS_VERSION,
    computedAt: Date.now(),
    sourcesUsed,
    extractionMethod: 'rules',
    _sourceQuality: sourceQuality,
    _needsLlm: needsLlm,
    _lowConfFields: lowConfFields,
  };
}

// ══════════════════════════════════════════════
// CONTENT HASH (for LLM cache)
// ══════════════════════════════════════════════

export async function hashText(text) {
  if (!text) return 'empty';
  const encoder = new TextEncoder();
  const data = encoder.encode(text.substring(0, 2000));
  const buf = await crypto.subtle.digest('SHA-256', data);
  const hex = Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  return hex.substring(0, 16);
}

export function minimalSignalsSnapshot(signals) {
  if (!signals) return null;
  return {
    industry: signals.industry || null,
    offerType: signals.offerType || null,
    b2b_b2c: signals.b2b_b2c || null,
    sizeProxy: signals.sizeProxy || null,
    tags: (signals.tags || []).slice(0, 5),
    language: signals.language || null,
    extractionMethod: signals.extractionMethod || null,
    sourceQuality: signals.sourceQuality ?? null,
    dataHash: signals.dataHash || null,
  };
}

function quickHash(text) {
  if (!text) return 'empty';
  const str = text.substring(0, 2000);
  let h1 = 0x811c9dc5, h2 = 0;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x5bd1e995);
  }
  return ((h1 >>> 0).toString(36) + (h2 >>> 0).toString(36));
}

// ══════════════════════════════════════════════
// MAIN: COMPUTE & STORE SIGNALS
// ══════════════════════════════════════════════

// Dedup guard: prevent concurrent writes for the same contact
const _inFlightClassifications = new Set();

/**
 * Checks if existing signals are fresh enough to skip re-computation.
 * Version mismatch triggers reclassification (e.g. when prepareText changes).
 */
export function signalsAreFresh(existingSignals, maxAgeMsOrContactData, contactDataArg) {
  if (!existingSignals || !existingSignals.computedAt) return false;
  if (existingSignals.signalsVersion !== SIGNALS_VERSION) return false;

  let maxAgeMs = 7 * 24 * 60 * 60 * 1000;
  let contactData = null;
  if (typeof maxAgeMsOrContactData === 'number') {
    maxAgeMs = maxAgeMsOrContactData;
    contactData = contactDataArg || null;
  } else if (typeof maxAgeMsOrContactData === 'object' && maxAgeMsOrContactData !== null) {
    contactData = maxAgeMsOrContactData;
  }

  if ((Date.now() - existingSignals.computedAt) >= maxAgeMs) return false;

  if (contactData && existingSignals.dataHash) {
    const { raw } = prepareText(contactData);
    const currentHash = quickHash(raw);
    if (currentHash !== existingSignals.dataHash) return false;
  }

  return true;
}

/**
 * Computes leadSignals for a contact and stores them on the document.
 * Uses rule-based extraction (Pass A). LLM fallback (Pass B) is
 * triggered via backend when needed.
 *
 * Protected against concurrent writes for the same contact.
 *
 * @param {string} userId
 * @param {string} contactId
 * @param {Object} contactData - the contact's current data
 * @param {Object} [options] - { forceLlm: false, skipStore: false }
 * @returns {Object} leadSignals
 */
export async function computeAndStoreSignals(userId, contactId, contactData, options = {}) {
  const lockKey = `${userId}:${contactId}`;

  if (_inFlightClassifications.has(lockKey)) {
    if (!options.force) return null;
    let waited = 0;
    while (_inFlightClassifications.has(lockKey) && waited < 10000) {
      await new Promise(r => setTimeout(r, 50));
      waited += 50;
    }
  }
  _inFlightClassifications.add(lockKey);

  try {
    if (!options.force && contactData.leadSignals && signalsAreFresh(contactData.leadSignals, contactData)) {
      return contactData.leadSignals;
    }

    const CORE_FIELDS = ['industry', 'offerType', 'b2b_b2c', 'sizeProxy'];
    const CONF_KEY = { industry: 'industryConf', offerType: 'offerTypeConf', b2b_b2c: 'b2bB2cConf', sizeProxy: 'sizeProxyConf' };
    const provenance = {};

    // ── Pass A: Rule-based extraction (free, instant) ──
    const signals = extractSignalsRuleBased(contactData);
    const { _allMatches, _needsLlm, _lowConfFields, _sourceQuality, ...cleanSignals } = signals;
    cleanSignals.sourceQuality = _sourceQuality.score;

    for (const f of CORE_FIELDS) {
      if (cleanSignals[f] && cleanSignals[f] !== 'unknown') provenance[f] = 'rules';
    }

    let extractionMethod = 'rules';

    // ── Pass R: Research-based extraction (structured data, high confidence) ──
    // Overrides Pass A when research provides higher-confidence signals.
    const researchResult = extractSignalsFromResearch(contactData);
    if (researchResult) {
      const { signals: rSignals, provenance: rProv } = researchResult;
      for (const f of CORE_FIELDS) {
        const ck = CONF_KEY[f];
        if (rSignals[f] && rSignals[f] !== 'unknown') {
          const rConf = rSignals[ck] || 0;
          const existingConf = cleanSignals[ck] || 0;
          if (rConf > existingConf) {
            cleanSignals[f] = rSignals[f];
            cleanSignals[ck] = rConf;
            provenance[f] = rProv[f] || 'research';
          }
        }
      }
      if (Object.keys(rProv).length > 0) extractionMethod = 'research+rules';
    }

    // ── Pass B: Backend Nano LLM fallback (only when A+R left gaps) ──
    const hasRealSignal = CORE_FIELDS
      .some(f => cleanSignals[f] && cleanSignals[f] !== 'unknown');

    const { raw } = prepareText(contactData);
    if (!hasRealSignal && raw.length >= 10) {
      const llmSignals = await classifyViaNanoBackend(raw, contactId);
      if (llmSignals) {
        for (const f of CORE_FIELDS) {
          if (llmSignals[f] && llmSignals[f] !== 'unknown' && (!cleanSignals[f] || cleanSignals[f] === 'unknown')) {
            cleanSignals[f] = llmSignals[f];
            cleanSignals[CONF_KEY[f]] = 0.75;
            provenance[f] = 'nano_llm';
          }
        }
        if (llmSignals.tags && llmSignals.tags.length > 0) {
          cleanSignals.tags = [...new Set([...(cleanSignals.tags || []), ...llmSignals.tags])].slice(0, 10);
        }
        extractionMethod = extractionMethod === 'research+rules' ? 'research+rules+llm' : 'nano_llm';
      }
    }

    cleanSignals.extractionMethod = extractionMethod;
    cleanSignals.signalProvenance = provenance;

    cleanSignals.dataHash = quickHash(raw);

    // Preserve human-corrected fields (highest priority — always wins)
    const existing = contactData.leadSignals || {};
    const confirmed = existing.humanConfirmedFields;
    if (confirmed && typeof confirmed === 'object') {
      for (const f of CORE_FIELDS) {
        if (confirmed[f] && existing[f] && existing[f] !== 'unknown') {
          cleanSignals[f] = existing[f];
          cleanSignals[CONF_KEY[f]] = 0.99;
          provenance[f] = 'human';
        }
      }
      cleanSignals.humanConfirmedFields = confirmed;
      cleanSignals.humanConfirmedAt = existing.humanConfirmedAt;
    }

    if (!options.skipStore) {
      const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
      await setDoc(contactRef, { leadSignals: cleanSignals }, { merge: true });
    }

    // Queue for backend LLM (Pass B) — only fields still unresolved after Pass A + R
    const remainingLowConf = (_lowConfFields || []).filter(f =>
      !cleanSignals[f] || cleanSignals[f] === 'unknown' || (cleanSignals[CONF_KEY[f]] || 0) < CONF_THRESHOLD
    );
    if (_needsLlm && remainingLowConf.length >= 2) {
      try {
        const hashedQueueId = await hashText(`${userId}:${contactId}`);
        const queueRef = doc(firestore, `World/${userId}/signalLlmQueue/${hashedQueueId}`);
        await setDoc(queueRef, {
          contactId,
          lowConfFields: remainingLowConf,
          sourceQuality: _sourceQuality?.score ?? 0,
          queuedAt: Date.now(),
          status: 'pending',
          retryCount: 0,
          signalsVersion: SIGNALS_VERSION,
        }, { merge: false });
      } catch (queueErr) {
        console.warn('[LeadSignals] signalLlmQueue write failed (non-critical):', queueErr.message);
      }
    }

    return { ...cleanSignals, _needsLlm, _lowConfFields, _sourceQuality };
  } catch (e) {
    console.warn('[LeadSignals] computeAndStoreSignals failed:', e.message);
    return null;
  } finally {
    _inFlightClassifications.delete(lockKey);
  }
}

/**
 * Batch-classify multiple contacts (for use after import).
 * Processes in chunks to avoid blocking the UI.
 *
 * @param {string} userId
 * @param {Array<{id: string, data: Object}>} contacts
 * @param {Function} [onProgress] - callback(processed, total)
 * @returns {Object} { processed, needsLlm, errors }
 */
export async function batchClassifyContacts(userId, contacts, onProgress) {
  const CHUNK_SIZE = 10;
  let processed = 0;
  let needsLlm = 0;
  let errors = 0;

  for (let i = 0; i < contacts.length; i += CHUNK_SIZE) {
    const chunk = contacts.slice(i, i + CHUNK_SIZE);

    const results = await Promise.allSettled(
      chunk.map(c => computeAndStoreSignals(userId, c.id, c.data))
    );

    for (const r of results) {
      if (r.status === 'fulfilled' && r.value) {
        processed++;
        if (r.value._needsLlm) needsLlm++;
      } else {
        errors++;
      }
    }

    if (onProgress) onProgress(processed + errors, contacts.length);

    // Yield to UI between chunks
    await new Promise(resolve => setTimeout(resolve, 50));
  }

  return { processed, needsLlm, errors, total: contacts.length };
}

// ══════════════════════════════════════════════
// LAZY CLASSIFICATION QUEUE
// Viewport-driven: only classify contacts the user actually sees.
// Deduped, throttled, and budget-capped per flush cycle.
// ══════════════════════════════════════════════

const _lazyQueue = new Map();
const _lazyProcessed = new Set();
let _lazyFlushTimer = null;
let _lazyFlushRunning = false;
let _lazyUserId = null;
let _onSignalsComputed = null;

const LAZY_FLUSH_DELAY_MS = 600;
const LAZY_BUDGET_PER_FLUSH = 15;
const LAZY_BUDGET_PER_SECOND = 5;

export function setOnSignalsComputed(callback) {
  _onSignalsComputed = callback;
}

const MIN_TEXT_FOR_CLASSIFICATION = 50;

export function enqueueForClassification(userId, contactId, contactData) {
  if (!userId || !contactId || !contactData) return;
  if (_budgetExceeded) return;
  if (_lazyProcessed.has(contactId)) return;
  if (contactData.leadSignals && signalsAreFresh(contactData.leadSignals, contactData)) return;

  const isEnriched = !!(contactData.spy_last_updated || contactData.spy_confidence_score > 0);
  if (!isEnriched) {
    const { raw } = prepareText(contactData);
    if (raw.length < MIN_TEXT_FOR_CLASSIFICATION) return;
  }

  _lazyUserId = userId;
  _lazyQueue.set(contactId, contactData);

  if (_lazyFlushTimer) clearTimeout(_lazyFlushTimer);
  _lazyFlushTimer = setTimeout(_flushLazyQueue, LAZY_FLUSH_DELAY_MS);
}

async function _flushLazyQueue() {
  if (_lazyFlushRunning || _lazyQueue.size === 0 || !_lazyUserId || _budgetExceeded) return;
  _lazyFlushRunning = true;

  const batch = [];
  for (const [id, data] of _lazyQueue) {
    if (batch.length >= LAZY_BUDGET_PER_FLUSH) break;
    batch.push({ id, data });
    _lazyQueue.delete(id);
  }

  const updates = [];
  for (let i = 0; i < batch.length; i += LAZY_BUDGET_PER_SECOND) {
    if (_budgetExceeded) break;
    const chunk = batch.slice(i, i + LAZY_BUDGET_PER_SECOND);
    const results = await Promise.allSettled(
      chunk.map(c => computeAndStoreSignals(_lazyUserId, c.id, c.data))
    );
    results.forEach((r, idx) => {
      if (r.status === 'fulfilled' && r.value) {
        _lazyProcessed.add(chunk[idx].id);
        const { _needsLlm, _lowConfFields, _sourceQuality, ...cleanSignals } = r.value;
        updates.push({ contactId: chunk[idx].id, signals: cleanSignals });
      }
    });
    if (i + LAZY_BUDGET_PER_SECOND < batch.length) {
      await new Promise(r => setTimeout(r, 1000));
    }
  }

  if (updates.length > 0 && _onSignalsComputed) {
    _onSignalsComputed(updates);
  }

  _lazyFlushRunning = false;

  if (_lazyQueue.size > 0 && !_budgetExceeded) {
    _lazyFlushTimer = setTimeout(_flushLazyQueue, LAZY_FLUSH_DELAY_MS);
  }
}

// ══════════════════════════════════════════════
// CORRECTION TRACKING — Human corrections become training data
// ══════════════════════════════════════════════

const CANONICAL_TEXT_VERSION = 'ct_v2';

// Derive safe evidence tokens from existing leadSignals (zero PII risk).
// Falls back to featuresHit keys if tags are empty.
function deriveEvidenceTokens(signals) {
  const tags = signals?.tags;
  if (Array.isArray(tags) && tags.length > 0) return tags.slice(0, 3);

  const tokens = [];
  if (signals?.industry && signals.industry !== 'unknown') tokens.push(signals.industry.toLowerCase());
  if (signals?.offerType && signals.offerType !== 'unknown') tokens.push(signals.offerType);
  if (signals?.b2b_b2c && signals.b2b_b2c !== 'unknown') tokens.push(signals.b2b_b2c);
  return tokens.slice(0, 3);
}

function buildFeaturesHit(signals) {
  const hits = [];
  if (signals?.industry && signals.industry !== 'unknown') hits.push(`industry:${signals.industry}`);
  if (signals?.offerType && signals.offerType !== 'unknown') hits.push(`offer:${signals.offerType}`);
  if (signals?.b2b_b2c && signals.b2b_b2c !== 'unknown') hits.push(`b2b:${signals.b2b_b2c}`);
  if (signals?.sizeProxy && signals.sizeProxy !== 'unknown') hits.push(`size:${signals.sizeProxy}`);
  if (signals?.tags?.length > 0) {
    for (const t of signals.tags.slice(0, 5)) hits.push(`tag:${t}`);
  }
  return hits;
}

/**
 * Records a human correction of a signal classification.
 * NO cleartext stored — only versioned hashes, safe tokens, and feature tags.
 */
export async function recordSignalCorrection(userId, contactId, field, oldValue, newValue, contactData, reasonTag, editorType = 'manual_ui') {
  try {
    if (!userId || !contactId || !field || oldValue === newValue) return;

    const { raw, sourcesUsed } = prepareText(contactData || {});
    const signals = contactData?.leadSignals || {};

    const correction = {
      hashedLeadId: await hashText(`${userId}:${contactId}`),
      field,
      oldValue: oldValue || 'unknown',
      newValue,
      canonicalTextHash: await hashText(`${CANONICAL_TEXT_VERSION}|${raw}`),
      canonicalTextVersion: CANONICAL_TEXT_VERSION,
      evidencePhrases: deriveEvidenceTokens(signals),
      featuresHit: buildFeaturesHit(signals),
      sourceQuality: signals.sourceQuality || scoreSourceQuality(raw, sourcesUsed[0]).score,
      sourcesUsed,
      editorType,
      reasonTag: reasonTag || null,
      correctedAt: Date.now(),
      signalsVersion: SIGNALS_VERSION,
    };

    const corrRef = doc(collection(firestore, `World/${userId}/signalCorrections`));
    await setDoc(corrRef, correction);

    // Update leadSignals: set value + mark field as human-confirmed
    const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
    const update = {};
    update[`leadSignals.${field}`] = newValue;
    update[`leadSignals.${field}Conf`] = 0.99;
    update[`leadSignals.humanConfirmedFields.${field}`] = true;
    update[`leadSignals.humanConfirmedAt`] = Date.now();
    await setDoc(contactRef, update, { merge: true });

    curateToFewShot(userId, field).catch(() => {});

    return true;
  } catch (e) {
    console.warn('[SignalCorrection] recordSignalCorrection failed:', e.message);
    return false;
  }
}

// ══════════════════════════════════════════════
// FEW-SHOT MEMORY — Curated, diverse, GDPR-clean exemplars
// ══════════════════════════════════════════════

const TRUSTED_THRESHOLD = 10;
const MAX_CORRECTIONS_PER_DAY = 20;

/**
 * Curates the top few-shot exemplars from recent corrections.
 * Stored in World/{userId}/signalFewShot/{field} as a single doc.
 * Only safe tokens + label, no cleartext.
 *
 * Guardrails:
 *   - Needs >= TRUSTED_THRESHOLD qualifying corrections before writing
 *   - Daily cap: max MAX_CORRECTIONS_PER_DAY per calendar day count
 *   - Dedup: one exemplar per label (diverse coverage)
 *   - supportCount: how many corrections back this label
 */
async function curateToFewShot(userId, field) {
  try {
    const corrRef = collection(firestore, `World/${userId}/signalCorrections`);
    const q = query(corrRef, orderBy('correctedAt', 'desc'), firestoreLimit(50));
    const snap = await getDocs(q);

    if (snap.empty) return;

    // Apply daily cap: max N corrections per calendar day
    const dayCounts = {};
    const capped = [];
    for (const d of snap.docs) {
      const data = d.data();
      if (data.field !== field || data.sourceQuality < 40 || !data.evidencePhrases?.length) continue;
      const dayKey = new Date(data.correctedAt).toISOString().slice(0, 10);
      dayCounts[dayKey] = (dayCounts[dayKey] || 0) + 1;
      if (dayCounts[dayKey] <= MAX_CORRECTIONS_PER_DAY) {
        capped.push(data);
      }
    }

    if (capped.length < TRUSTED_THRESHOLD) return;

    // Count support per label, then pick one diverse exemplar each
    const labelSupport = {};
    for (const c of capped) {
      labelSupport[c.newValue] = (labelSupport[c.newValue] || 0) + 1;
    }

    const seen = new Set();
    const exemplars = [];
    for (const c of capped) {
      if (seen.has(c.newValue)) continue;
      seen.add(c.newValue);
      exemplars.push({
        evidencePhrases: c.evidencePhrases,
        featuresHit: c.featuresHit || [],
        label: c.newValue,
        sourceQuality: c.sourceQuality,
        supportCount: labelSupport[c.newValue] || 1,
        createdAt: c.correctedAt,
      });
      if (exemplars.length >= 8) break;
    }

    const fewShotRef = doc(firestore, `World/${userId}/signalFewShot/${field}`);
    await setDoc(fewShotRef, {
      exemplars,
      totalCorrections: capped.length,
      curatedAt: Date.now(),
      version: SIGNALS_VERSION,
    });
  } catch (e) {
    console.warn('[FewShot] curateToFewShot failed:', e.message);
  }
}
